import { DOCUMENT } from '@angular/common';
import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, Input, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HTMLElementUtils } from '@cinetixx/cinetixx-ui';
import { filter, fromEvent, tap } from 'rxjs';
import { RotationCursorSvgBuilder } from '../builders';
import { DragStates } from '../enums';
import { ShapeAttributesManager } from '../managers';
import { CreatorActionsService, CreatorStateService } from '../services/app-services';
import { CursorType } from '../types';
import { cloneDeep } from 'lodash';

@Directive({
  selector: '[cxRotationElement]',
  standalone: true
})
export class RotationElementDirective implements AfterViewInit {

  @Input() elementToRotate: HTMLElement;

  private _initialAngle: number;
  private readonly _rotationCursorSvgBuilder = new RotationCursorSvgBuilder();
  private readonly _elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly _document = inject(DOCUMENT);
  private readonly _destroyRef= inject(DestroyRef);
  private readonly _renderer = inject(Renderer2);

  public constructor() {
    this.elementToRotate = this._elementRef.nativeElement;
  }

  public ngAfterViewInit(): void {
    fromEvent<MouseEvent>(this._document, 'mousemove').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter(() =>
        ShapeAttributesManager.getDragState(this._elementRef.nativeElement) === DragStates.SELECTED
        || ShapeAttributesManager.getDragState(this._elementRef.nativeElement) === DragStates.ROTATE
      )
    ).subscribe((event: MouseEvent) => {
      this.rotateElement(event);
      this.updateCursor(event);
    });

    fromEvent<MouseEvent>(this._elementRef.nativeElement, 'mousedown').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter((event: MouseEvent) =>
        ShapeAttributesManager.getDragState(this._elementRef.nativeElement) === DragStates.SELECTED
        && !(event.target as HTMLElement).closest('.resize-point')
        && !this.getCursorType(event.clientX, event.clientY).includes('default')
      )
    ).subscribe(this.startRotation.bind(this));

    fromEvent<MouseEvent>(this._document, 'mouseup').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter(() => ShapeAttributesManager.getDragState(this._elementRef.nativeElement) === DragStates.ROTATE)
    ).subscribe(this.mouseUpHandler.bind(this));
  }

  private get currentRotation(): number {
    return HTMLElementUtils.getRotationDegrees(this.elementToRotate) ?? 0;
  }

  private startRotation(event: MouseEvent): void {
    ShapeAttributesManager.setDragState(this._elementRef.nativeElement, DragStates.ROTATE);
    this._initialAngle = this.getShapeAngle(event.clientX, event.clientY);
  }

  private getShapeAngle(x: number, y: number): number {
    const rect = this._elementRef.nativeElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
  }

  private getCursorAngle(x: number, y: number): number {
    const shapeRect = this.elementToRotate.getBoundingClientRect();
    const shapeCenterX = shapeRect.left + shapeRect.width / 2;
    const shapeCenterY = shapeRect.top + shapeRect.height / 2;
    return Math.atan2(y - shapeCenterY, x - shapeCenterX) * (180 / Math.PI);
  }

  private rotateElement(event: MouseEvent): void {
    if (ShapeAttributesManager.getDragState(this._elementRef.nativeElement) === DragStates.ROTATE) {
      const currentAngle = this.getShapeAngle(event.clientX, event.clientY);
      const rotation = currentAngle - this._initialAngle;
      this.elementToRotate.style.transform = `rotate(${this.currentRotation + rotation}deg)`;
      this._initialAngle = currentAngle;
    }
  }

  private updateCursor(event: MouseEvent): void {
    const cursorType: CursorType = this.getCursorType(event.clientX, event.clientY);
    const isActiveRotate = ShapeAttributesManager.getDragState(this._elementRef.nativeElement) === DragStates.ROTATE;

    if (cursorType === 'default' && !isActiveRotate) {
      this._renderer.setStyle(this._elementRef.nativeElement, 'cursor', 'auto');
    } else {
      const cursorAngle = this.getCursorAngle(event.clientX, event.clientY);

      this._renderer.setStyle(
        this._elementRef.nativeElement,
        'cursor',
        `url('data:image/svg+xml;base64,${this.getRotationCursor(cursorAngle)}') 10 10, auto`
      );
    }
  }

  private getRotationCursor(angle: number = 0): CursorType {
    return this._rotationCursorSvgBuilder.withRotation(angle).build();
  }

  private mouseUpHandler(): void {
    ShapeAttributesManager.setDragState(this._elementRef.nativeElement, DragStates.SELECTED);
    const previous = cloneDeep(CreatorStateService.priorityShape.rotate);
    const changed = previous !== this.currentRotation;
    CreatorStateService.priorityShape.rotate = this.currentRotation;

    if (changed) {
      CreatorActionsService.priorityShapeRotationChanged({ previous, current: CreatorStateService.priorityShape.rotate }, { emitEvent: true });
    }
  }

  private getCursorType(x: number, y: number): CursorType {
    const rect = this._elementRef.nativeElement.getBoundingClientRect();
    const cornerThreshold = 25;
    const rotateCursor = this.getRotationCursor(this.currentRotation);

    if (x >= rect.left - cornerThreshold && x <= rect.left &&
        y >= rect.top - cornerThreshold && y <= rect.top) {
      return rotateCursor; /** Left top corner */
    } else if (x >= rect.right && x <= rect.right + cornerThreshold &&
               y >= rect.top - cornerThreshold && y <= rect.top) {
      return rotateCursor; /** Right top corner */
    } else if (x >= rect.left - cornerThreshold && x <= rect.left &&
               y >= rect.bottom && y <= rect.bottom + cornerThreshold) {
      return rotateCursor; /** Left bottom corner */
    } else if (x >= rect.right && x <= rect.right + cornerThreshold &&
               y >= rect.bottom && y <= rect.bottom + cornerThreshold) {
      return rotateCursor; /** Right bottom corner */
    }

    return 'default';
  }
}
